import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(description: string, lineSpacing: number, bScale: number) {
  if (!description) return null;
  const lines = description.split('\n').map((line) => line.trim()).filter(Boolean);
  const style = { fontSize: `${0.75 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="mt-1 whitespace-pre-line" style={style}>{description}</p>;
  }
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-5" style={style}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*\s]+/, '')}</li>)}
    </ul>
  );
}

export function AtsTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;
  const contact = [personal.email, personal.phone, personal.location, personal.website, personal.linkedin].filter(Boolean);

  return (
    <div
      className="bg-white text-black h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="border-b-2 border-black pb-3" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-bold uppercase tracking-wide" style={{ fontSize: `${1.5 * hScale}rem` }}>{personal.fullName || 'Your Name'}</h1>
        {contact.length > 0 && <p className="mt-1" style={{ fontSize: `${0.75 * bScale}rem` }}>{contact.join(' | ')}</p>}
      </header>

      <main className="pt-4" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <h2 className="border-b border-black pb-1 font-bold uppercase tracking-wider" style={{ fontSize: `${0.75 * hScale}rem` }}>Professional Summary</h2>
            <p className="mt-1.5 whitespace-pre-line" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="border-b border-black pb-1 font-bold uppercase tracking-wider" style={{ fontSize: `${0.75 * hScale}rem` }}>Experience</h2>
            <div className="mt-2" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((item) => (
                <article key={item.id}>
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-bold" style={{ fontSize: `${0.875 * hScale}rem` }}>{item.title}</h3>
                    <span className="shrink-0" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(item.startDate, s.dateFormat)} - {item.current ? 'Present' : formatDate(item.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="font-semibold" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.company}{item.location && `, ${item.location}`}</p>
                  {renderDescription(item.description, s.spacing.lineSpacing, bScale)}
                </article>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="border-b border-black pb-1 font-bold uppercase tracking-wider" style={{ fontSize: `${0.75 * hScale}rem` }}>Education</h2>
            <div className="mt-2" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((item) => (
                <article key={item.id}>
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-bold" style={{ fontSize: `${0.875 * hScale}rem` }}>{item.school}</h3>
                    <span className="shrink-0" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(item.startDate, s.dateFormat)} - {formatDate(item.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p style={{ fontSize: `${0.75 * bScale}rem` }}>{item.degree}{item.field && `, ${item.field}`}{item.location && ` | ${item.location}`}</p>
                  {item.description && <p className="mt-0.5 whitespace-pre-line" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{item.description}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="border-b border-black pb-1 font-bold uppercase tracking-wider" style={{ fontSize: `${0.75 * hScale}rem` }}>Skills</h2>
            <p className="mt-1.5" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{skills.map((skill) => skill.name).join(', ')}</p>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="border-b border-black pb-1 font-bold uppercase tracking-wider" style={{ fontSize: `${0.75 * hScale}rem` }}>Projects</h2>
            <div className="mt-2" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((project) => (
                <article key={project.id}>
                  <h3 className="font-bold" style={{ fontSize: `${0.875 * hScale}rem` }}>{project.name}{project.url && ` | ${project.url}`}</h3>
                  {project.description && <p className="mt-0.5 whitespace-pre-line" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{project.description}</p>}
                  {project.technologies.length > 0 && <p style={{ fontSize: `${0.75 * bScale}rem` }}><span className="font-semibold">Technologies:</span> {project.technologies.join(', ')}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="border-b border-black pb-1 font-bold uppercase tracking-wider" style={{ fontSize: `${0.75 * hScale}rem` }}>Certifications</h2>
            <div className="mt-1.5" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.titleGap, fontSize: `${0.75 * bScale}rem` }}>
              {certifications.map((certification) => (
                <p key={certification.id}><span className="font-semibold">{certification.name}</span> | {certification.issuer}{certification.date && ` | ${certification.date}`}{certification.url && ` | ${certification.url}`}</p>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
